<template>
    <div class="delete-modal-backdrop" @click.self="cancel">
        <div class="delete-modal">
            <header class="modal-header">
                <h3 class="is-size-5"><i class="fad fa-trash mr-2 has-text-danger"></i>Delete '{{ file.title }}'</h3>
            </header>
            <section class="modal-body">
                <div v-if="loading" class="has-text-centered p-4">
                    <i class="fas fa-spinner fa-spin-pulse"></i> Checking for references...
                </div>
                <div v-else>
                    <p v-if="!usages.length" class="mb-3">
                        No references found. This file will be moved to the trash.
                    </p>
                    <div v-else>
                        <b-notification type="is-warning" :closable="false" class="mb-3">
                            <strong>This file is referenced in {{ usages.length }}
                                {{ usages.length === 1 ? 'location' : 'locations' }}.</strong>
                            Deleting will not remove it from these references and may cause
                            broken media. Usage detection is best-effort; other references may exist.
                        </b-notification>
                        <ul class="usage-list">
                            <li v-for="(u, i) in usages" :key="i">
                                <i class="fad fa-fw mr-2" :class="iconFor(u.type)"></i>
                                <span class="usage-type">{{ labelFor(u.type) }}:</span>
                                <span class="usage-title">{{ u.title || '(Untitled)' }}</span>
                                <span class="usage-id has-text-grey">#{{ u.id }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>
            <footer class="modal-footer">
                <button class="button" @click="cancel" :disabled="deleting">Cancel</button>
                <button class="button is-danger" @click="confirm" :disabled="loading || deleting">
                    <i class="fas fa-spinner fa-spin-pulse mr-2" v-if="deleting"></i>
                    <i class="fad fa-trash mr-2" v-else></i>
                    Delete
                </button>
            </footer>
        </div>
    </div>
</template>

<script>
    import { client as http } from "@/http_client";

    const TYPE_META = {
        conf_session: { icon: 'fa-calendar-day', label: 'Conference Session (media)' },
        conf_session_attachment: { icon: 'fa-paperclip', label: 'Conference Session (attachment)' },
        conf_session_intro: { icon: 'fa-film', label: 'Conference Session (intro video)' },
        component: { icon: 'fa-cube', label: 'Lesson Component' },
        course: { icon: 'fa-graduation-cap', label: 'Course' },
        organization: { icon: 'fa-building', label: 'Organization (logo)' }
    };

    export default {
        props: {
            file: { type: Object, required: true }
        },
        data() {
            return {
                loading: true,
                deleting: false,
                usages: []
            };
        },
        methods: {
            iconFor(type) {
                return (TYPE_META[type] && TYPE_META[type].icon) || 'fa-file';
            },
            labelFor(type) {
                return (TYPE_META[type] && TYPE_META[type].label) || type;
            },
            cancel() {
                if (this.deleting) return;
                this.$emit('cancel');
            },
            confirm() {
                this.deleting = true;
                http.delete('library/' + this.file.id).then(() => {
                    this.$emit('deleted', this.file);
                }).catch(err => {
                    console.error(err);
                    this.$buefy.toast.open({ message: 'Failed to delete file', type: 'is-danger' });
                    this.deleting = false;
                });
            }
        },
        mounted() {
            http.get('library/' + this.file.id + '/usage').then(response => {
                this.usages = response.data.usages || [];
            }).catch(err => {
                console.error(err);
                this.$buefy.toast.open({ message: 'Failed to check references', type: 'is-warning' });
            }).finally(() => {
                this.loading = false;
            });
        }
    };
</script>

<style lang="scss" scoped>
    .delete-modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(10, 10, 10, 0.6);
        z-index: 2000;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .delete-modal {
        background: white;
        border-radius: 6px;
        width: 100%;
        max-width: 560px;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    }

    .modal-header {
        padding: 1rem 1.25rem;
        border-bottom: 1px solid #e7e7e7;

        h3 {
            margin: 0;
        }
    }

    .modal-body {
        padding: 1.25rem;
        overflow-y: auto;
        flex: 1;
    }

    .modal-footer {
        padding: 0.75rem 1.25rem;
        border-top: 1px solid #e7e7e7;
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
    }

    .usage-list {
        list-style: none;
        margin: 0;
        padding: 0;
        max-height: 300px;
        overflow-y: auto;
        border: 1px solid #e7e7e7;
        border-radius: 4px;

        li {
            padding: 0.5rem 0.75rem;
            border-bottom: 1px solid #f1f1f1;

            &:last-child {
                border-bottom: none;
            }
        }

        .usage-type {
            font-weight: 600;
            margin-right: 0.25rem;
        }

        .usage-id {
            font-size: 0.8rem;
            margin-left: 0.5rem;
        }
    }
</style>
